import { useRouter } from 'next/router';
import {
  filterValuesSeparator,
  filterValuesSeparatorSplitRegex
} from 'shared-helpers/consts';
import { useReduxState } from 'hooks';
import { augmentFiltersWithCustomAttributes } from 'lib/customAttributes';
import { Button } from 'components/common';
import { StatCount } from 'components/common/stats';
import { PageSideNavFiltersInstance } from '.';

interface Props {
  filterCustomAttributes?: {
    key: string;
    values: string[];
  }[];
  filters?: { [groupHeader: string]: Record<'label' | 'value', string>[] };
  filtersSeparate?: {
    [groupHeader: string]: Record<'label' | 'value', string>[];
  };
  filtersWithRequireAll?: string[];
  shallowNavigation?: boolean;
}

const serializePresentInAllParam = (fields: string[]) =>
  fields.length ? `&presentInAll=${fields.join(filterValuesSeparator)}` : '';

const PageSideNavFilters = ({
  filterCustomAttributes,
  filters = null,
  filtersSeparate = null,
  filtersWithRequireAll = [],
  shallowNavigation = false
}: Props) => {
  const { language, messages, serverConfig, theme } = useReduxState();

  const router = useRouter();

  const getBaseUrl = () => {
    const filterKeys = Object.keys(filtersByKey);
    const baseParams = Object.keys(router.query).filter(
      (param) => !filterKeys.includes(param) && param !== 'presentInAll'
    );

    return (
      router.pathname +
      (baseParams.length
        ? '?' +
          baseParams.reduce(
            (params, param) => `${params}&${param}=${router.query[param]}`,
            ''
          )
        : '')
    );
  };

  // default is to have all filters under a single "Filters" title
  // a server config may be used to separate some filters so that
  // they are displayed above all other filters under a separate title

  const activeFilters: string[] = [];
  const activeFiltersWithoutSeparate: string[] = [];
  const activeFiltersForSeparateKeys: { [key: string]: string[] } = {};
  const activeRequireAllKeys =
    typeof router.query.presentInAll === 'string'
      ? router.query.presentInAll.split(filterValuesSeparatorSplitRegex)
      : [];
  const filterKeyByGroup: { [groupHeader: string]: string } = {};
  const separateFilterKeys: string[] = [];

  const customAttributeFilters = augmentFiltersWithCustomAttributes({
    filterCustomAttributes,
    language,
    messages,
    serverConfig,
    type:
      router.pathname === '/courses'
        ? 'course'
        : router.pathname === '/paths'
        ? 'path'
        : router.pathname === '/achievements'
        ? router.query.type === 'individual'
          ? 'individualAchievement'
          : 'achievement'
        : null
  });

  const combinedFilters = {
    ...customAttributeFilters.atStart,
    ...(filters || {}),
    ...customAttributeFilters.atEnd
  };

  const combinedFiltersWithSeparateFilters = {
    ...combinedFilters,
    ...(filtersSeparate || {})
  };

  const filtersByKey = Object.keys(combinedFiltersWithSeparateFilters).reduce(
    (allFilters, filter) => {
      const values = combinedFiltersWithSeparateFilters[filter];
      if (!values.length) return allFilters;

      let key: RegExpMatchArray | string = values[0].value.match(/&(.*)=.*/);
      if (!key[1]) return;

      key = key[1];
      filterKeyByGroup[filter] = key;
      allFilters[key] = [];

      if (filtersSeparate?.[filter]) {
        separateFilterKeys.push(key);
      }

      const queryValues: string[] = Array.isArray(router.query[key])
        ? (router.query[key] as string[])
        : [router.query[key] as string];
      values.forEach(({ label, value }) => {
        if (!label) return;

        const filteredValue = decodeURIComponent(value.replace(`&${key}=`, ''));
        if (queryValues.includes(filteredValue)) {
          activeFilters.push(value);
          if (filtersSeparate?.[filter]) {
            if (!activeFiltersForSeparateKeys[filter]) {
              activeFiltersForSeparateKeys[filter] = [];
            }
            activeFiltersForSeparateKeys[filter].push(value);
          } else {
            activeFiltersWithoutSeparate.push(value);
          }
        }
        allFilters[key].push(filteredValue);
      });

      return allFilters;
    },
    {} as { [key: string]: string[] }
  );

  const handleClearFilters = (separateGroupHeader?: string) => {
    // default is to clear all filters
    let url = getBaseUrl();

    const activeSeparateGroups = Object.keys(activeFiltersForSeparateKeys);

    let remainingRequireAllKeys: string[];

    if (separateGroupHeader) {
      // clear only filter for a separate filter
      const separateFilterKey = filterKeyByGroup[separateGroupHeader];
      remainingRequireAllKeys = activeRequireAllKeys.filter(
        (key) => key !== separateFilterKey
      );

      url +=
        activeFiltersWithoutSeparate.join('') +
        activeSeparateGroups
          .filter((group) => group !== separateGroupHeader)
          .map((group) => activeFiltersForSeparateKeys[group].join(''))
          .join('') +
        serializePresentInAllParam(remainingRequireAllKeys);
    } else if (activeSeparateGroups.length) {
      remainingRequireAllKeys = activeRequireAllKeys.filter((key) =>
        separateFilterKeys.includes(key)
      );

      url +=
        activeSeparateGroups
          .map((group) => activeFiltersForSeparateKeys[group].join(''))
          .join('') + serializePresentInAllParam(remainingRequireAllKeys);
    }

    router.push(url, url, shallowNavigation ? { shallow: true } : {});
  };

  const availableFiltersExist = Object.keys(
    combinedFiltersWithSeparateFilters || {}
  ).some((key) => combinedFiltersWithSeparateFilters[key]?.length);
  if (!availableFiltersExist) return null;

  return (
    <div className="filters">
      {Object.keys(filtersSeparate || {}).length
        ? Object.keys(filtersSeparate).map((group) => {
            const key = filterKeyByGroup[group];

            return (
              <div className="filters-separate" key={key}>
                <div className="filter-controls">
                  <div className="filter-title">
                    {group}

                    <StatCount
                      category={
                        activeFiltersForSeparateKeys[group]?.length
                          ? 'success'
                          : 'default'
                      }
                      value={activeFiltersForSeparateKeys[group]?.length || 0}
                    />
                  </div>

                  {activeFiltersForSeparateKeys[group]?.length ? (
                    <div className="filter-clear">
                      <Button
                        category="lighter"
                        icon="times"
                        isRounded={true}
                        onClick={() => handleClearFilters(group)}
                        size="small"
                        style={{ padding: '2px 7px 3px 4px' }}
                        text={messages.commonClear}
                      />
                    </div>
                  ) : null}
                </div>

                <PageSideNavFiltersInstance
                  activeFilters={activeFilters}
                  activeRequireAllKeys={activeRequireAllKeys}
                  filter={group}
                  filterKey={key}
                  filters={filtersSeparate}
                  getBaseUrl={getBaseUrl}
                  isRequireAllEnabled={filtersWithRequireAll.includes(key)}
                  isSeparateFilter={true}
                  key={key}
                  shallowNavigation={shallowNavigation}
                />
              </div>
            );
          })
        : null}

      <div className="filter-controls">
        <div className="filter-title">
          {messages.commonFilters}

          <StatCount
            category={
              activeFiltersWithoutSeparate.length ? 'success' : 'default'
            }
            value={activeFiltersWithoutSeparate.length}
          />
        </div>

        {activeFiltersWithoutSeparate.length ? (
          <div className="filter-clear">
            <Button
              category="lighter"
              icon="times"
              isRounded={true}
              onClick={() => handleClearFilters()}
              size="small"
              style={{ padding: '2px 7px 3px 4px' }}
              text={messages.commonClear}
            />
          </div>
        ) : null}
      </div>

      <div className="filter-groups">
        {Object.keys(combinedFilters).map((group) => {
          const key = filterKeyByGroup[group];

          return Boolean(combinedFilters[group].length) ? (
            <PageSideNavFiltersInstance
              activeFilters={activeFilters}
              activeRequireAllKeys={activeRequireAllKeys}
              filter={group}
              filterKey={key}
              filters={combinedFilters}
              getBaseUrl={getBaseUrl}
              isRequireAllEnabled={filtersWithRequireAll.includes(key)}
              key={key}
              shallowNavigation={shallowNavigation}
            />
          ) : null;
        })}
      </div>

      <style jsx>{`
        .filters {
          color: ${theme.textColor};
          font-size: 1.4rem;
          font-weight: 600;
          margin-top: 20px;
        }
        .filter-controls {
          align-items: center;
          display: flex;
          justify-content: space-between;
          padding: 0 7px;
        }
        .filter-title {
          align-items: flex-end;
          color: ${theme.textColorLight};
          display: flex;
          font-size: 1.4rem;
          font-weight: 700;
          gap: 10px;
          padding: 2px 0;
        }
        .filter-clear {
          padding-top: 1px;
        }
        .filters-separate {
          margin-bottom: 30px;
        }
      `}</style>
    </div>
  );
};

export default PageSideNavFilters;
